<template>
    <div v-if="canViewAgent">
        <button class="chat-toggle" type="button" aria-label="Open or close KPAW Assistant" @click="toggleChat">
            <span v-if="!open" class="kai-button-text">KAI</span>
            <span v-else>✕</span>
        </button>

        <transition name="chat-pop">
            <div
                v-if="open"
                class="chat-box"
                :class="[
                    `chat-box--${selectedView}`,
                    {
                        'chat-box--resizing': isResizing,
                    },
                ]"
                :style="chatBoxStyle"
            >
                <button
                    v-if="selectedView === 'custom'"
                    class="resize-handle"
                    type="button"
                    aria-label="Resize chat widget"
                    title="Drag to resize"
                    @mousedown.prevent="startMouseResize"
                    @touchstart.passive="startTouchResize"
                >
                    <span class="resize-handle-indicator"></span>
                </button>

                <div class="header">
                    <div class="brand">
                        <div class="avatar">K</div>

                        <div>
                            <div class="title">KPAW Assistant</div>

                            <div class="status">
                                <span class="status-dot"></span>
                                Online
                            </div>
                        </div>
                    </div>

                    <div class="header-actions">
                        <div ref="viewDropdownRef" class="view-dropdown">
                            <button
                                class="view-dropdown-button"
                                type="button"
                                title="Change chat view"
                                aria-label="Change chat view"
                                :aria-expanded="showViewDropdown"
                                @click.stop="toggleViewDropdown"
                            >
                                <span class="view-dropdown-button-icon">
                                    {{ selectedViewIcon }}
                                </span>

                                <span class="view-dropdown-chevron">
                                    {{ showViewDropdown ? '▴' : '▾' }}
                                </span>
                            </button>

                            <transition name="dropdown-pop">
                                <div v-if="showViewDropdown" class="view-dropdown-menu">
                                    <div class="view-dropdown-title">Switch to</div>

                                    <button
                                        v-for="option in viewOptions"
                                        :key="option.value"
                                        type="button"
                                        class="view-dropdown-option"
                                        :class="{
                                            'view-dropdown-option--active': selectedView === option.value,
                                        }"
                                        @click.stop="selectView(option.value)"
                                    >
                                        <span class="view-option-content">
                                            <span class="view-option-icon" aria-hidden="true">
                                                {{ option.icon }}
                                            </span>

                                            <span>{{ option.label }}</span>
                                        </span>

                                        <span v-if="selectedView === option.value" class="view-option-check"> ✓ </span>
                                    </button>
                                </div>
                            </transition>
                        </div>

                        <button class="close-btn" type="button" aria-label="Close chat" @click="toggleChat">✕</button>
                    </div>
                </div>

                <div ref="messagesContainer" class="messages">
                    <div v-if="messages.length === 0" class="welcome">
                        <div class="welcome-icon">🤖</div>

                        <div class="welcome-title">Hi, I’m KPAW Assistant</div>

                        <div class="welcome-subtitle">Ask me anything about KPAW.</div>
                    </div>

                    <div
                        v-for="(msg, index) in messages"
                        :key="index"
                        class="message-row"
                        :class="msg.role === 'user' ? 'user-row' : 'assistant-row'"
                    >
                        <div v-if="msg.role !== 'user'" class="bot-icon">🤖</div>

                        <div class="bubble" :class="msg.role === 'user' ? 'user-bubble' : 'assistant-bubble'">
                            <div v-if="msg.loading" class="typing">
                                <span></span>
                                <span></span>
                                <span></span>
                            </div>

                            <template v-else-if="msg.role === 'user'">
                                {{ msg.content }}
                            </template>

                            <div v-else class="markdown-content" v-html="msg.content"></div>
                        </div>

                        <div v-if="msg.role === 'user'" class="user-icon">
                            {{ getUserInitials() }}
                        </div>
                    </div>
                </div>

                <div class="input-area">
                    <div class="input-bar">
                        <input
                            v-model="input"
                            :disabled="loading"
                            placeholder="Ask something..."
                            @keyup.enter="sendMessage"
                        />

                        <button
                            type="button"
                            aria-label="Send message"
                            :disabled="loading || !input.trim()"
                            @click="sendMessage"
                        >
                            ➤
                        </button>
                    </div>
                </div>
            </div>
        </transition>
    </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { includes, some } from 'lodash';
import { storeToRefs } from 'pinia';

import useAuthStore from '@/modules/auth/store/auth.store';
import { PermissionService } from '@/modules/auth/services/permission.service';
import { KaiService } from '@/shared/services/kai.service';

const permissionServices = new PermissionService();
const kaiService = new KaiService();
const authStore = useAuthStore();

const { selectAccessTokenRoles, selectAccessTokenUserName } = storeToRefs(authStore);

const canViewAgent = computed(() =>
    some(selectAccessTokenRoles.value ?? [], (role) =>
        includes(permissionServices.getPermissionRoles('agent.read'), role)
    )
);

type ChatMessage = {
    role: 'user' | 'assistant';
    content: string;
    loading?: boolean;
};

type ChatView = 'floating' | 'custom' | 'sidebar' | 'fullscreen';

type ChatViewOption = {
    label: string;
    value: ChatView;
    icon: string;
};

const MIN_CUSTOM_WIDTH = 340;
const MAX_CUSTOM_WIDTH = 900;
const DEFAULT_CUSTOM_WIDTH = 520;
const CHAT_RIGHT_OFFSET = 24;
const CUSTOM_WIDTH_STORAGE_KEY = 'kpaw-chat-custom-width';

const open = ref(false);
const input = ref('');
const loading = ref(false);
const messages = ref<ChatMessage[]>([]);
const messagesContainer = ref<HTMLElement | null>(null);
const viewDropdownRef = ref<HTMLElement | null>(null);
const welcomeShown = ref(false);
// Foundry response id of the last turn — chains server-side conversation state.
const previousResponseId = ref<string | null>(null);

const selectedView = ref<ChatView>('floating');
const showViewDropdown = ref(false);
const customWidth = ref(DEFAULT_CUSTOM_WIDTH);
const isResizing = ref(false);

const viewOptions: ChatViewOption[] = [
    {
        label: 'Floating',
        value: 'floating',
        icon: '▣',
    },
    {
        label: 'Custom',
        value: 'custom',
        icon: '↔',
    },
    {
        label: 'Sidebar',
        value: 'sidebar',
        icon: '◧',
    },
    {
        label: 'Full screen',
        value: 'fullscreen',
        icon: '⌗',
    },
];

const selectedViewIcon = computed(() => {
    return viewOptions.find((option) => option.value === selectedView.value)?.icon ?? '▣';
});

const chatBoxStyle = computed<Record<string, string> | undefined>(() => {
    if (selectedView.value !== 'custom') {
        return undefined;
    }

    return {
        width: `${customWidth.value}px`,
    };
});

const getMaximumCustomWidth = (): number => {
    return Math.min(MAX_CUSTOM_WIDTH, Math.max(MIN_CUSTOM_WIDTH, window.innerWidth - CHAT_RIGHT_OFFSET * 2));
};

const clampCustomWidth = (width: number): number => {
    return Math.min(Math.max(width, MIN_CUSTOM_WIDTH), getMaximumCustomWidth());
};

const saveCustomWidth = (): void => {
    try {
        localStorage.setItem(CUSTOM_WIDTH_STORAGE_KEY, String(customWidth.value));
    } catch {
        // Ignore storage errors.
    }
};

const restoreCustomWidth = (): void => {
    try {
        const savedWidth = Number(localStorage.getItem(CUSTOM_WIDTH_STORAGE_KEY));

        if (Number.isFinite(savedWidth)) {
            customWidth.value = clampCustomWidth(savedWidth);
        }
    } catch {
        customWidth.value = DEFAULT_CUSTOM_WIDTH;
    }
};

const toggleChat = async (): Promise<void> => {
    open.value = !open.value;
    showViewDropdown.value = false;

    if (!open.value) {
        stopResize();
        return;
    }

    if (!welcomeShown.value && messages.value.length === 0) {
        messages.value.push({
            role: 'assistant',
            content: '👋 Hello, I am KPAW AI. How can I help you today?',
        });

        welcomeShown.value = true;

        await scrollToBottom();
    }
};

const toggleViewDropdown = (): void => {
    showViewDropdown.value = !showViewDropdown.value;
};

const selectView = async (view: ChatView): Promise<void> => {
    stopResize();

    selectedView.value = view;
    showViewDropdown.value = false;

    if (view === 'custom') {
        customWidth.value = clampCustomWidth(customWidth.value);
    }

    await scrollToBottom();
};

const handleOutsideClick = (event: MouseEvent): void => {
    const target = event.target as Node;

    if (viewDropdownRef.value && !viewDropdownRef.value.contains(target)) {
        showViewDropdown.value = false;
    }
};

const handleEscapeKey = (event: KeyboardEvent): void => {
    if (event.key !== 'Escape') {
        return;
    }

    if (isResizing.value) {
        stopResize();
        return;
    }

    if (showViewDropdown.value) {
        showViewDropdown.value = false;
        return;
    }

    if (open.value) {
        open.value = false;
    }
};

const setCustomWidthFromClientX = (clientX: number): void => {
    const requestedWidth = window.innerWidth - clientX - CHAT_RIGHT_OFFSET;

    customWidth.value = clampCustomWidth(requestedWidth);
};

const resizeWithMouse = (event: MouseEvent): void => {
    if (!isResizing.value) {
        return;
    }

    setCustomWidthFromClientX(event.clientX);
};

const resizeWithTouch = (event: TouchEvent): void => {
    if (!isResizing.value || event.touches.length === 0) {
        return;
    }

    event.preventDefault();

    setCustomWidthFromClientX(event.touches[0].clientX);
};

const startMouseResize = (event: MouseEvent): void => {
    if (selectedView.value !== 'custom') {
        return;
    }

    isResizing.value = true;
    showViewDropdown.value = false;

    document.addEventListener('mousemove', resizeWithMouse);

    document.addEventListener('mouseup', stopResize);

    document.body.style.cursor = 'ew-resize';
    document.body.style.userSelect = 'none';

    setCustomWidthFromClientX(event.clientX);
};

const startTouchResize = (event: TouchEvent): void => {
    if (selectedView.value !== 'custom' || event.touches.length === 0) {
        return;
    }

    isResizing.value = true;
    showViewDropdown.value = false;

    document.addEventListener('touchmove', resizeWithTouch, {
        passive: false,
    });

    document.addEventListener('touchend', stopResize);

    document.addEventListener('touchcancel', stopResize);

    document.body.style.userSelect = 'none';

    setCustomWidthFromClientX(event.touches[0].clientX);
};

function stopResize(): void {
    if (!isResizing.value) {
        return;
    }

    isResizing.value = false;

    document.removeEventListener('mousemove', resizeWithMouse);

    document.removeEventListener('mouseup', stopResize);

    document.removeEventListener('touchmove', resizeWithTouch);

    document.removeEventListener('touchend', stopResize);

    document.removeEventListener('touchcancel', stopResize);

    document.body.style.cursor = '';
    document.body.style.userSelect = '';

    saveCustomWidth();
}

const handleWindowResize = (): void => {
    if (selectedView.value !== 'custom') {
        return;
    }

    customWidth.value = clampCustomWidth(customWidth.value);
};

const getUserInitials = (): string => {
    const name = selectAccessTokenUserName.value?.trim();

    if (!name) {
        return 'U';
    }

    return name
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0].toUpperCase())
        .join('');
};

const scrollToBottom = async (): Promise<void> => {
    await nextTick();

    messagesContainer.value?.scrollTo({
        top: messagesContainer.value.scrollHeight,
        behavior: 'smooth',
    });
};

const renderMarkdown = (content: string): string => {
    return DOMPurify.sanitize(marked.parse(content) as string);
};

const sendMessage = async (): Promise<void> => {
    if (!input.value.trim() || loading.value) {
        return;
    }

    const userText = input.value.trim();

    messages.value.push({
        role: 'user',
        content: userText,
    });

    input.value = '';
    loading.value = true;

    const typingIndex =
        messages.value.push({
            role: 'assistant',
            content: '',
            loading: true,
        }) - 1;

    await scrollToBottom();

    try {
        // Server-side state: send only the new message; Foundry chains from
        // previous_response_id (null on the first turn).
        const { reply, responseId } = await kaiService.sendMessage(userText, previousResponseId.value ?? undefined);

        if (responseId) {
            previousResponseId.value = responseId;
        }

        messages.value[typingIndex] = {
            role: 'assistant',
            content: renderMarkdown(reply),
        };
    } catch {
        messages.value[typingIndex] = {
            role: 'assistant',
            content: 'Error reaching AI service',
        };
    } finally {
        loading.value = false;
        await scrollToBottom();
    }
};

onMounted(() => {
    restoreCustomWidth();

    document.addEventListener('click', handleOutsideClick);

    document.addEventListener('keydown', handleEscapeKey);

    window.addEventListener('resize', handleWindowResize);
});

onBeforeUnmount(() => {
    document.removeEventListener('click', handleOutsideClick);

    document.removeEventListener('keydown', handleEscapeKey);

    window.removeEventListener('resize', handleWindowResize);

    stopResize();
});
</script>

<style scoped lang="scss">
.chat-toggle {
    position: fixed;
    right: 24px;
    bottom: 24px;
    width: 72px;
    height: 56px;
    border: none;
    border-radius: 14px;
    background: #0d8a96;
    color: #ffffff;
    cursor: pointer;
    z-index: 10001;
    box-shadow: 0 10px 25px rgba(13, 138, 150, 0.35);
    display: flex;
    align-items: center;
    justify-content: center;
    transition:
        transform 0.2s ease,
        box-shadow 0.2s ease;
}

.chat-toggle:hover {
    transform: translateY(-2px);
    box-shadow: 0 14px 32px rgba(13, 138, 150, 0.45);
}

.kai-button-text {
    font-size: 18px;
    font-weight: 700;
    letter-spacing: 0.5px;
}

.chat-box {
    position: fixed;
    background: #ffffff;
    overflow: hidden;
    z-index: 10000;
    display: flex;
    flex-direction: column;
    border: 1px solid rgba(226, 232, 240, 0.9);
    transition:
        width 0.25s ease,
        height 0.25s ease,
        right 0.25s ease,
        bottom 0.25s ease,
        border-radius 0.25s ease,
        box-shadow 0.25s ease;
}

.chat-box--floating {
    right: 24px;
    bottom: 96px;
    width: 380px;
    height: 560px;
    border-radius: 22px;
    box-shadow: 0 24px 70px rgba(15, 23, 42, 0.28);
}

.chat-box--custom {
    right: 24px;
    bottom: 96px;
    height: 560px;
    min-width: 340px;
    max-width: calc(100vw - 48px);
    border-radius: 22px;
    overflow: visible;
    box-shadow: 0 24px 70px rgba(15, 23, 42, 0.28);
}

.chat-box--custom .header {
    border-radius: 22px 22px 0 0;
}

.chat-box--custom .messages {
    overflow-x: hidden;
}

.chat-box--custom .input-area {
    border-radius: 0 0 22px 22px;
}

.chat-box--sidebar {
    top: 0;
    right: 0;
    bottom: 0;
    width: 420px;
    height: 100vh;
    border-radius: 0;
    border-top: none;
    border-right: none;
    border-bottom: none;
    box-shadow: -16px 0 45px rgba(15, 23, 42, 0.2);
}

.chat-box--fullscreen {
    inset: 0;
    width: 100%;
    height: 100vh;
    border: none;
    border-radius: 0;
    box-shadow: none;
}

.chat-box--resizing {
    transition: none;
}

.resize-handle {
    position: absolute;
    top: 0;
    left: -7px;
    width: 14px;
    height: 100%;
    padding: 0;
    border: none;
    outline: none;
    background: transparent;
    cursor: ew-resize;
    touch-action: none;
    z-index: 10;
    display: flex;
    align-items: center;
    justify-content: center;
}

.resize-handle::before {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 6px;
    width: 2px;
    content: '';
    background: transparent;
    transition: background 0.15s ease;
}

.resize-handle:hover::before,
.chat-box--resizing .resize-handle::before {
    background: rgba(194, 56, 3, 0.55);
}

.resize-handle-indicator {
    width: 5px;
    height: 52px;
    border-radius: 999px;
    background: #cbd5e1;
    box-shadow: 0 2px 8px rgba(15, 23, 42, 0.15);
    transition:
        width 0.15s ease,
        background 0.15s ease;
}

.resize-handle:hover .resize-handle-indicator,
.chat-box--resizing .resize-handle-indicator {
    width: 6px;
    background: #c23803;
}

.header {
    min-height: 74px;
    padding: 14px 16px;
    background: #c23803;
    color: #ffffff;
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-shrink: 0;
}

.brand {
    min-width: 0;
    display: flex;
    align-items: center;
    gap: 12px;
}

.avatar {
    width: 42px;
    height: 42px;
    border-radius: 14px;
    background: rgba(255, 255, 255, 0.2);
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 800;
    font-size: 20px;
    backdrop-filter: blur(8px);
    flex-shrink: 0;
}

.title {
    font-size: 16px;
    font-weight: 800;
    line-height: 1.1;
    white-space: nowrap;
}

.status {
    margin-top: 4px;
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    opacity: 0.9;
}

.status-dot {
    width: 8px;
    height: 8px;
    background: #22c55e;
    border-radius: 50%;
    box-shadow: 0 0 0 4px rgba(34, 197, 94, 0.2);
}

.header-actions {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
}

.view-dropdown {
    position: relative;
}

.view-dropdown-button,
.close-btn {
    height: 34px;
    border: none;
    border-radius: 10px;
    cursor: pointer;
    color: #ffffff;
    background: rgba(255, 255, 255, 0.18);
    transition:
        background 0.15s ease,
        transform 0.15s ease;
}

.view-dropdown-button:hover,
.close-btn:hover {
    background: rgba(255, 255, 255, 0.28);
}

.view-dropdown-button:active,
.close-btn:active {
    transform: scale(0.96);
}

.view-dropdown-button {
    min-width: 54px;
    padding: 0 9px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
}

.view-dropdown-button-icon {
    font-size: 17px;
    line-height: 1;
}

.view-dropdown-chevron {
    font-size: 11px;
    line-height: 1;
}

.close-btn {
    width: 34px;
    font-size: 16px;
}

.view-dropdown-menu {
    position: absolute;
    top: calc(100% + 12px);
    right: 0;
    width: 194px;
    padding: 8px;
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    box-shadow: 0 16px 38px rgba(15, 23, 42, 0.22);
    z-index: 10002;
}

.view-dropdown-menu::before {
    position: absolute;
    top: -6px;
    right: 20px;
    width: 12px;
    height: 12px;
    content: '';
    background: #ffffff;
    border-top: 1px solid #e2e8f0;
    border-left: 1px solid #e2e8f0;
    transform: rotate(45deg);
}

.view-dropdown-title {
    padding: 4px 8px 7px;
    color: #64748b;
    font-size: 12px;
    font-weight: 700;
}

.view-dropdown-option {
    width: 100%;
    min-height: 38px;
    padding: 8px 9px;
    border: none;
    border-radius: 7px;
    background: transparent;
    color: #475569;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 14px;
    text-align: left;
    transition:
        color 0.15s ease,
        background 0.15s ease;
}

.view-dropdown-option:hover {
    background: #f1f5f9;
    color: #0f172a;
}

.view-dropdown-option--active {
    background: #e8f1ff;
    color: #2563eb;
    font-weight: 600;
}

.view-dropdown-option--active:hover {
    background: #dbeafe;
    color: #2563eb;
}

.view-option-content {
    display: flex;
    align-items: center;
    gap: 10px;
}

.view-option-icon {
    width: 18px;
    color: currentColor;
    font-size: 17px;
    line-height: 1;
    display: inline-flex;
    align-items: center;
    justify-content: center;
}

.view-option-check {
    color: #2563eb;
    font-size: 17px;
    font-weight: 700;
}

.messages {
    flex: 1;
    min-height: 0;
    padding: 18px 14px;
    overflow-y: auto;
    background: #ffffff;
}

.chat-box--fullscreen .messages {
    width: 100%;
    max-width: 1000px;
    margin: 0 auto;
    padding: 28px 24px;
}

.messages::-webkit-scrollbar {
    width: 6px;
}

.messages::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 999px;
}

.welcome {
    margin: 36px auto;
    text-align: center;
    color: #334155;
    max-width: 260px;
}

.welcome-icon {
    font-size: 38px;
    margin-bottom: 10px;
}

.welcome-title {
    font-size: 17px;
    font-weight: 800;
    margin-bottom: 6px;
}

.welcome-subtitle {
    font-size: 13px;
    color: #64748b;
}

.message-row {
    display: flex;
    align-items: flex-end;
    gap: 8px;
    margin-bottom: 14px;
}

.user-row {
    justify-content: flex-end;
}

.assistant-row {
    justify-content: flex-start;
}

.bot-icon,
.user-icon {
    width: 30px;
    height: 30px;
    border-radius: 50%;
    background: #ffffff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    box-shadow: 0 4px 12px rgba(15, 23, 42, 0.12);
    flex-shrink: 0;
}

.bubble {
    max-width: 80%;
    padding: 12px 14px;
    font-size: 14px;
    line-height: 1.45;
    word-break: break-word;
    overflow-wrap: anywhere;
}

.user-bubble {
    background: #c23803;
    color: #ffffff;
    border-radius: 16px 16px 4px 16px;
    box-shadow: 0 8px 20px rgba(194, 56, 3, 0.25);

    /* Preserve manually entered user line breaks only */
    white-space: pre-wrap;
}

.assistant-bubble {
    background: #ffffff;
    color: #0f172a;
    border-radius: 16px 16px 16px 4px;
    border: 1px solid #e2e8f0;
    box-shadow: 0 8px 20px rgba(15, 23, 42, 0.08);

    /*
     * Critical fix:
     * Do not preserve whitespace between generated HTML elements.
     */
    white-space: normal;
}

.chat-box--fullscreen .bubble {
    max-width: 70%;
}

.typing {
    display: flex;
    gap: 5px;
    align-items: center;
    padding: 4px 2px;
}

.typing span {
    width: 7px;
    height: 7px;
    background: #64748b;
    border-radius: 50%;
    animation: bounce 1.2s infinite ease-in-out;
}

.typing span:nth-child(2) {
    animation-delay: 0.15s;
}

.typing span:nth-child(3) {
    animation-delay: 0.3s;
}

.input-area {
    padding: 13px;
    background: #ffffff;
    border-top: 1px solid #e2e8f0;
    flex-shrink: 0;
}

.chat-box--fullscreen .input-bar {
    width: calc(100% - 48px);
    max-width: 1000px;
    margin: 0 auto;
}

.input-bar {
    display: flex;
    align-items: center;
    gap: 9px;
    background: #f8fafc;
    border: 1px solid #dbe3ef;
    border-radius: 16px;
    padding: 7px;
}

.input-bar:focus-within {
    border-color: #c23803;
    box-shadow: 0 0 0 3px rgba(194, 56, 3, 0.1);
}

.input-bar input {
    flex: 1;
    min-width: 0;
    border: none;
    outline: none;
    background: transparent;
    padding: 9px 8px;
    font-size: 14px;
    color: #0f172a;
}

.input-bar input::placeholder {
    color: #94a3b8;
}

.input-bar button {
    width: 40px;
    height: 40px;
    border: none;
    border-radius: 13px;
    color: #ffffff;
    background: #c23803;
    cursor: pointer;
    font-size: 18px;
    transition:
        transform 0.15s ease,
        opacity 0.15s ease;
}

.input-bar button:hover:not(:disabled) {
    transform: scale(1.04);
}

.input-bar button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.chat-pop-enter-active,
.chat-pop-leave-active {
    transition:
        opacity 0.22s ease,
        transform 0.22s ease;
}

.chat-pop-enter-from,
.chat-pop-leave-to {
    opacity: 0;
    transform: translateY(14px) scale(0.96);
}

.dropdown-pop-enter-active,
.dropdown-pop-leave-active {
    transition:
        opacity 0.15s ease,
        transform 0.15s ease;
    transform-origin: top right;
}

.dropdown-pop-enter-from,
.dropdown-pop-leave-to {
    opacity: 0;
    transform: translateY(-5px) scale(0.97);
}

@keyframes bounce {
    0%,
    80%,
    100% {
        transform: translateY(0);
        opacity: 0.45;
    }

    40% {
        transform: translateY(-5px);
        opacity: 1;
    }
}

.markdown-content {
    font-size: 14px;
    line-height: 1.45;
    color: #0f172a;
}

.markdown-content :deep(p) {
    margin: 0 0 4px;
}

.markdown-content :deep(h1),
.markdown-content :deep(h2),
.markdown-content :deep(h3),
.markdown-content :deep(h4),
.markdown-content :deep(h5),
.markdown-content :deep(h6) {
    margin: 6px 0 2px;
    line-height: 1.15;
    font-weight: 700;
}

.markdown-content :deep(h1),
.markdown-content :deep(h2),
.markdown-content :deep(h3) {
    display: inline-flex;
    align-items: center;
    margin: 12px 0 6px;
    padding: 4px 10px;
    border-radius: 6px;
    background: #f8dfd5;
    color: #c23803;
    font-weight: 700;
    line-height: 1.25;
}

.markdown-content :deep(h1) {
    font-size: 17px;
}

.markdown-content :deep(h2) {
    font-size: 16px;
}

.markdown-content :deep(h3) {
    font-size: 15px;
}

.markdown-content :deep(ul),
.markdown-content :deep(ol) {
    margin: 2px 0;
    padding-left: 18px;
}

.markdown-content :deep(li) {
    margin: 0;
    padding: 0;
    line-height: 1.4;
}

.markdown-content :deep(li + li) {
    margin-top: 2px;
}

/* remove huge markdown gaps */
.markdown-content :deep(p + ul),
.markdown-content :deep(p + ol),
.markdown-content :deep(ul + p),
.markdown-content :deep(ol + p),
.markdown-content :deep(h1 + p),
.markdown-content :deep(h2 + p),
.markdown-content :deep(h3 + p),
.markdown-content :deep(h4 + p),
.markdown-content :deep(h5 + p),
.markdown-content :deep(h6 + p) {
    margin-top: 0;
}

/* fix list paragraph spacing */
.markdown-content :deep(li p) {
    margin: 0;
}

/* code */
.markdown-content :deep(code) {
    background: #f1f5f9;
    color: #e11d48;
    padding: 1px 4px;
    border-radius: 4px;
    font-size: 13px;
}

/* code block */
.markdown-content :deep(pre) {
    margin: 4px 0;
    padding: 8px 10px;
    border-radius: 8px;
    overflow-x: auto;
}

/* blockquote */
.markdown-content :deep(blockquote) {
    margin: 4px 0;
    padding-left: 8px;
}

/* table */
.markdown-content :deep(table) {
    margin: 4px 0;
}

.markdown-content :deep(th),
.markdown-content :deep(td) {
    padding: 4px 6px;
}

/* section headings */
.markdown-content :deep(h2),
.markdown-content :deep(h3) {
    display: inline-block;
    padding: 4px 12px;
    margin: 12px 0 8px;
    background: #fff1ea;
    color: #c23803;
    border-radius: 8px;
    font-size: 16px;
    font-weight: 700;
}

/* specification rows */
.markdown-content :deep(ul) {
    list-style: none;
    padding: 0;
    margin: 0;
}

.markdown-content :deep(li) {
    padding: 6px 0;
    border-bottom: 1px solid #f1f5f9;
}

.markdown-content :deep(li:last-child) {
    border-bottom: none;
}

@media (max-width: 768px) {
    .chat-box--sidebar {
        width: 100%;
    }

    .chat-box--fullscreen .messages {
        padding: 20px 14px;
    }

    .chat-box--fullscreen .input-bar {
        width: 100%;
    }

    .chat-box--fullscreen .bubble {
        max-width: 80%;
    }
}

@media (max-width: 480px) {
    .chat-box--floating,
    .chat-box--custom {
        right: 12px;
        left: 12px;
        bottom: 88px;
        width: auto !important;
        min-width: 0;
        max-width: none;
        height: 75vh;
        overflow: hidden;
    }

    .chat-box--sidebar,
    .chat-box--fullscreen {
        inset: 0;
        width: 100%;
        height: 100dvh;
    }

    .resize-handle {
        display: none;
    }

    .chat-toggle {
        right: 18px;
        bottom: 18px;
    }

    .header {
        padding: 12px;
    }

    .avatar {
        width: 38px;
        height: 38px;
        border-radius: 12px;
        font-size: 18px;
    }

    .title {
        font-size: 14px;
    }

    .view-dropdown-menu {
        position: fixed;
        top: 72px;
        right: 12px;
        width: 194px;
    }

    .view-dropdown-menu::before {
        display: none;
    }

    .bubble {
        max-width: 82%;
        padding: 12px 14px;
    }

    .markdown-content {
        line-height: 1.6;
    }
}
</style>
